/**
 * Командная «Очередь заказов» (team-portal, orders-core): заказы всех клиентов (без черновиков);
 * у сотрудника — только закреплённые за ним (team-visibility, ограничивает сервер).
 * Строки — TeamOrderRow (владелец, услуга, объём, статус данных, текущий этап); заказы, ожидающие
 * действия команды (awaitingTeam: «На проверке» или «На заполнении» без договора и счёта),
 * визуально выделены. Фильтры: статус данных,
 * услуга и обязательный фильтр/поиск по клиенту (select по uuid + текстовый поиск `q` — по
 * наименованию, ИНН или № заказа).
 */
import { useEffect, useState } from 'react'
import { ChevronDown, ChevronUp, Inbox } from 'lucide-react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  teamApi,
  type ClientSummary,
  type OrdersQuery,
  type Service,
  type TeamOrderRow,
} from '@/lib/api'
import { cn } from '@/lib/utils'
import { getErrorMessage } from '@/lib/errors'
import {
  STAGES_BY_SERVICE,
  compositionSummary,
  serviceLabel,
  stageFilterOptions,
  teamStatusBadge,
} from '@/lib/orders'
import { ClientSearch } from '@/company/client-search'
import { TrainingSubscriptionOrderBadge } from '@/training/training-subscription'
import { PageHead } from '@/design-system/components/layout/page-head'
import { Panel } from '@/design-system/components/layout/panel'
import { Toolbar } from '@/design-system/components/layout/toolbar'
import { Badge } from '@/design-system/components/primitives/badge'
import { Alert } from '@/design-system/components/feedback/alert'
import { EmptyState } from '@/design-system/components/feedback/empty-state'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/design-system/components/primitives/select'
import { Pagination } from '@/design-system/components/data/pagination'
import { useSeesAllClients } from '@/team/roles'
import { staffOptions, useStaff } from '@/team/use-staff'
import {
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableHeaderCell,
  DataTableRow,
} from '@/design-system/components/data/data-table'

const ANY = 'any'

/** Размер страницы очереди (требование: не более 20 заказов на странице). */
const PAGE_SIZE = 20

/**
 * Сколько клиентов грузим в выпадающий фильтр: ограничения «не более 20» больше нет
 * (centrika-docs!48), 100 — максимум страницы списка на сервере. Найти клиента за его
 * пределами можно поиском по наименованию или ИНН — он идёт на сервер по всей базе.
 */
const CLIENTS_LIMIT = 100

/** Статусы фильтра — без `draft` (черновики команде не видны). */
const STATUS_OPTIONS = [
  { value: ANY, label: 'Любой статус' },
  { value: 'filling', label: 'На заполнении' },
  { value: 'review', label: 'На проверке' },
  { value: 'changes_requested', label: 'Требуются правки' },
  { value: 'in_progress', label: 'В работе' },
  { value: 'done', label: 'Готово' },
  { value: 'cancelled', label: 'Отменён' },
]

/** Все услуги — для этапов фильтра без выбранной услуги. */
const ALL_SERVICES = Object.keys(STAGES_BY_SERVICE) as Service[]

const SERVICE_OPTIONS = [
  { value: ANY, label: 'Все услуги' },
  { value: 'sout', label: 'СОУТ' },
  { value: 'training', label: 'Обучение' },
  { value: 'profrisk', label: 'Профриски' },
  { value: 'ecology', label: 'Экология' },
]

function formatDate(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

type SortField = NonNullable<OrdersQuery['sort']>
type SortDir = NonNullable<OrdersQuery['dir']>

/**
 * Заголовок сортируемого столбца очереди (centrika-docs!77): название + пара компактных
 * кнопок-иконок «по возрастанию»/«по убыванию». Активные поле и направление выделены
 * цветом; у каждой кнопки — текстовая подпись aria-label и aria-pressed для СБ. Выбор
 * поля/направления единый на всю таблицу — одновременно активен только один вариант.
 */
function SortableHeader({
  label,
  field,
  activeField,
  activeDir,
  onSort,
}: {
  label: string
  field: SortField
  activeField: SortField
  activeDir: SortDir
  onSort: (field: SortField, dir: SortDir) => void
}) {
  function btn(dir: SortDir, Icon: typeof ChevronUp, text: string) {
    const active = activeField === field && activeDir === dir
    return (
      <button
        type="button"
        aria-label={`${text}: ${label}`}
        aria-pressed={active}
        onClick={() => onSort(field, dir)}
        className={cn(
          'rounded p-0.5 transition-colors hover:bg-hover',
          active ? 'text-accent' : 'text-txt-tertiary',
        )}
      >
        <Icon className="size-3.5" aria-hidden />
      </button>
    )
  }
  return (
    <span className="inline-flex items-center gap-1 whitespace-nowrap">
      {label}
      <span className="inline-flex items-center">
        {btn('asc', ChevronUp, 'Сортировать по возрастанию')}
        {btn('desc', ChevronDown, 'Сортировать по убыванию')}
      </span>
    </span>
  )
}

export function OrdersQueuePage() {
  const navigate = useNavigate()
  // Фильтры, поиск, сортировка и страница — в адресе страницы (training-followups), как у «Мои
  // заказы» клиента: «Назад» из заказа возвращает очередь в том же виде.
  const [searchParams, setSearchParams] = useSearchParams()
  const param = (key: string, fallback: string) => searchParams.get(key) || fallback
  const [status, setStatus] = useState(() => param('status', ANY))
  const [service, setService] = useState(() =>
    Object.keys(STAGES_BY_SERVICE).includes(param('service', '')) ? param('service', ANY) : ANY,
  )
  // Этап «Ход работ» (orders-list-filters; ТЗ п.8.1) — и без выбора услуги (ответ заказчицы 04.11):
  // тогда этапы всех услуг, «Заявка» и «Договор» — общие.
  const [stage, setStage] = useState(() => param('stage', ANY))
  const stages = stageFilterOptions(service === ANY ? undefined : (service as Service), ALL_SERVICES)
  const seesAll = useSeesAllClients()
  const [client, setClient] = useState(() => param('client', ANY))
  // Фильтр по ответственному от Центрики (team-access): любой / без ответственного / сотрудник.
  const [responsible, setResponsible] = useState(() => param('responsible', ANY))
  const staff = useStaff()
  const [q, setQ] = useState(() => param('q', ''))
  const [debouncedQ, setDebouncedQ] = useState(() => param('q', ''))
  // По умолчанию (первое открытие) — сортировка по дате отправки на проверку по убыванию:
  // позднее отправленные заказы сверху (centrika-docs!77).
  const [sortField, setSortField] = useState<SortField>(() =>
    param('sort', 'submitted') === 'created' ? 'created' : 'submitted',
  )
  const [sortDir, setSortDir] = useState<SortDir>(() => (param('dir', 'desc') === 'asc' ? 'asc' : 'desc'))
  const [clients, setClients] = useState<ClientSummary[]>([])
  const [items, setItems] = useState<TeamOrderRow[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(() => Math.max(1, Number(param('page', '1')) || 1))
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Состояние очереди — в адрес (replace: «Назад» из заказа ведёт в очередь, а не по шагам фильтров).
  useEffect(() => {
    const next = new URLSearchParams()
    if (status !== ANY) next.set('status', status)
    if (service !== ANY) next.set('service', service)
    if (stage !== ANY) next.set('stage', stage)
    if (client !== ANY) next.set('client', client)
    if (responsible !== ANY) next.set('responsible', responsible)
    if (debouncedQ) next.set('q', debouncedQ)
    if (sortField !== 'submitted') next.set('sort', sortField)
    if (sortDir !== 'desc') next.set('dir', sortDir)
    if (page > 1) next.set('page', String(page))
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true })
    // searchParams — только для сравнения; источник истины — состояние страницы.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, service, stage, client, responsible, debouncedQ, sortField, sortDir, page])

  // Каталог клиентов для фильтра по uuid.
  useEffect(() => {
    const controller = new AbortController()
    teamApi
      // Ограничения «не более 20 клиентов» в списке фильтра больше нет (centrika-docs!48).
      .listClients({ limit: CLIENTS_LIMIT }, controller.signal)
      .then((res) => {
        if (!controller.signal.aborted) setClients(res.items)
      })
      .catch(() => undefined)
    return () => controller.abort()
  }, [])

  // Дебаунс текстового поиска клиента (q), чтобы не дёргать сеть на каждый ввод. Очистка
  // поля применяется сразу, а любое изменение запроса открывает первую страницу очереди.
  useEffect(() => {
    const next = q.trim()
    // Запрос не изменился (открыли страницу с ним из адреса) — страницу не сбрасываем.
    if (next === debouncedQ) return
    if (next === '') {
      setDebouncedQ('')
      setPage(1)
      return
    }
    const t = setTimeout(() => {
      setDebouncedQ(next)
      setPage(1)
    }, 300)
    return () => clearTimeout(t)
    // debouncedQ — только для сравнения с вводом.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q])

  // Смена фильтра тоже открывает первую страницу — сбрасываем ПРЯМО в обработчике, а не
  // эффектом: иначе на смену фильтра уходил бы лишний запрос со старым номером страницы.
  function changeFilter(apply: () => void) {
    apply()
    setPage(1)
  }

  // Смена поля/направления сортировки: таблица пересортировывается на сервере, фильтры
  // сохраняются (это отдельный state), пагинация возвращается на первую страницу.
  function changeSort(field: SortField, dir: SortDir) {
    setSortField(field)
    setSortDir(dir)
    setPage(1)
  }

  useEffect(() => {
    const controller = new AbortController()
    setLoading(true)
    setError(null)
    teamApi
      .listOrders(
        {
          status: status === ANY ? undefined : status,
          service: service === ANY ? undefined : service,
          stage: stage === ANY ? undefined : stage,
          client: client === ANY ? undefined : client,
          responsible: responsible === ANY ? undefined : responsible,
          q: debouncedQ || undefined,
          sort: sortField,
          dir: sortDir,
          limit: PAGE_SIZE,
          offset: (page - 1) * PAGE_SIZE,
        },
        controller.signal,
      )
      .then((res) => {
        if (controller.signal.aborted) return
        // Страница «уехала» за пределы результатов (фильтр сузил очередь) — на первую.
        if (res.items.length === 0 && page > 1) {
          setPage(1)
          return
        }
        setItems(res.items)
        setTotal(res.total)
      })
      .catch((err) => {
        if (controller.signal.aborted) return
        setError(getErrorMessage(err, 'Не удалось загрузить очередь'))
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [status, service, stage, client, responsible, debouncedQ, sortField, sortDir, page])

  return (
    <div>
      <PageHead
        title="Очередь заказов"
        subtitle={
          seesAll
            ? 'Заказы всех клиентов (без черновиков)'
            : 'Заказы, где вы ответственный, и заказы ваших клиентов (без черновиков)'
        }
      />

      <Toolbar aria-label="Фильтры очереди">
        <ClientSearch
          value={q}
          onChange={setQ}
          placeholder="Поиск по названию, ИНН или № заказа"
          ariaLabel="Поиск заказа"
          className="min-w-48 flex-1"
        />
        <Select value={client} onValueChange={(v) => changeFilter(() => setClient(v))}>
          <SelectTrigger className="w-56 shrink-0" aria-label="Клиент">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>Все клиенты</SelectItem>
            {clients.map((c) => (
              // ИНН — второй строкой варианта менее заметным текстом; без ИНН строки нет.
              <SelectItem key={c.id} value={c.id} hint={c.inn}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={responsible} onValueChange={(v) => changeFilter(() => setResponsible(v))}>
          <SelectTrigger className="w-64 shrink-0" aria-label="Ответственный">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>Любой ответственный</SelectItem>
            <SelectItem value="none">Без ответственного</SelectItem>
            {staffOptions(staff).map((o) => (
              <SelectItem key={o.id} value={o.id}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={(v) => changeFilter(() => setStatus(v))}>
          <SelectTrigger className="w-48 shrink-0" aria-label="Статус данных">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={service}
          onValueChange={(v) =>
            changeFilter(() => {
              setService(v)
              // Этап остаётся, если он есть и у новой услуги («Договор»).
              const keep = stageFilterOptions(v === ANY ? undefined : (v as Service), ALL_SERVICES).some(
                (st) => st.key === stage,
              )
              if (!keep) setStage(ANY)
            })
          }
        >
          <SelectTrigger className="w-48 shrink-0" aria-label="Услуга">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SERVICE_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={stage} onValueChange={(v) => changeFilter(() => setStage(v))}>
          <SelectTrigger className="w-60 shrink-0" aria-label="Этап">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>Любой этап</SelectItem>
            {stages.map((st) => (
              <SelectItem key={st.key} value={st.key}>
                {st.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Toolbar>

      <Panel>
        {error ? (
          <Alert variant="danger" className="m-4">
            {error}
          </Alert>
        ) : loading ? (
          <p className="px-4 py-10 text-center text-txt-secondary">Загрузка…</p>
        ) : items.length === 0 ? (
          <EmptyState
            icon={Inbox}
            title="Очередь пуста"
            description="Заказы по фильтрам не найдены"
          />
        ) : (
          <DataTable>
            <DataTableHead>
              <DataTableRow>
                {/* «№ заказа» — первая колонка очереди (centrika-docs!50). */}
                <DataTableHeaderCell>№ заказа</DataTableHeaderCell>
                <DataTableHeaderCell>Клиент</DataTableHeaderCell>
                <DataTableHeaderCell>Услуга</DataTableHeaderCell>
                <DataTableHeaderCell>Объём</DataTableHeaderCell>
                <DataTableHeaderCell>Статус данных</DataTableHeaderCell>
                <DataTableHeaderCell>Этап</DataTableHeaderCell>
                <DataTableHeaderCell>
                  <SortableHeader
                    label="Дата создания"
                    field="created"
                    activeField={sortField}
                    activeDir={sortDir}
                    onSort={changeSort}
                  />
                </DataTableHeaderCell>
                <DataTableHeaderCell>
                  <SortableHeader
                    label="Дата отправки на проверку"
                    field="submitted"
                    activeField={sortField}
                    activeDir={sortDir}
                    onSort={changeSort}
                  />
                </DataTableHeaderCell>
              </DataTableRow>
            </DataTableHead>
            <DataTableBody>
              {items.map((o) => {
                const badge = teamStatusBadge(o.dataStatus)
                return (
                  <DataTableRow
                    key={o.id}
                    clickable
                    onClick={() => navigate(`/team/orders/${o.id}`)}
                  >
                    <DataTableCell className="whitespace-nowrap text-txt-secondary">
                      {o.number}
                    </DataTableCell>
                    <DataTableCell
                      className={o.awaitingTeam ? 'font-semibold text-txt' : 'font-medium'}
                    >
                      <span className="flex items-center gap-2">
                        {o.ownerName || o.ownerClientId}
                        {o.awaitingTeam ? <Badge status="warning">ждёт команду</Badge> : null}
                        {/* Изменения состава на согласовании (training-change-approval). */}
                        {o.pendingChange === 'awaiting_team' ? (
                          <Badge status="info">изменения клиента</Badge>
                        ) : o.pendingChange === 'awaiting_client' ? (
                          <Badge status="neutral">ждёт согласования клиента</Badge>
                        ) : null}
                      </span>
                      {/* ИНН организации второй строкой менее заметным текстом
                          (centrika-docs!51) — как в выпадающем фильтре «Клиент». У физлица
                          и компании без ИНН строки нет. Вне жирного начертания строки
                          «ждёт команду»: выделяется наименование, а не реквизит. */}
                      {o.ownerInn ? (
                        <span className="block text-12 font-normal text-txt-tertiary">
                          {o.ownerInn}
                        </span>
                      ) : null}
                    </DataTableCell>
                    <DataTableCell>
                      {serviceLabel(o.service)}
                      {/* Заказ по абонентскому обслуживанию по обучению — вторая строка ячейки. */}
                      {o.trainingSubscription ? (
                        <span className="mt-1 block">
                          <TrainingSubscriptionOrderBadge short />
                        </span>
                      ) : null}
                    </DataTableCell>
                    <DataTableCell className="text-txt-secondary">
                      {/* СОУТ (centrika-docs!63): объём = число рабочих мест числительным
                          (0 рабочих мест у пустого состава); прочие услуги — текстовая сводка. */}
                      {o.service === 'sout'
                        ? compositionSummary('sout', o.itemCount)
                        : o.volumeSummary || '—'}
                    </DataTableCell>
                    <DataTableCell>
                      <Badge status={badge.role}>{badge.label}</Badge>
                      {/* «Заполнено N из M» (training-listener-completeness) — пока клиент
                          довносит данные слушателей. */}
                      {o.service === 'training' &&
                      (o.dataStatus === 'filling' || o.dataStatus === 'changes_requested') &&
                      (o.listenersTotal ?? 0) > 0 ? (
                        <span className="mt-1 block whitespace-nowrap text-12 text-txt-tertiary">
                          Заполнено {o.listenersComplete ?? 0} из {o.listenersTotal}
                        </span>
                      ) : null}
                    </DataTableCell>
                    <DataTableCell className="text-txt-secondary">
                      {o.currentStage?.label ?? '—'}
                      {/* Ответственный от Центрики (team-access) — второй строкой: отдельная
                          колонка расширила бы таблицу, которая на узких экранах и так
                          прокручивается. */}
                      <span className="block text-12 text-txt-tertiary">
                        {o.responsibleName ? `Отв.: ${o.responsibleName}` : 'Без ответственного'}
                      </span>
                    </DataTableCell>
                    <DataTableCell className="whitespace-nowrap text-txt-secondary">
                      {formatDate(o.createdAt)}
                    </DataTableCell>
                    {/* Дата последней отправки на проверку; прочерк, если заказ ни разу
                        не отправляли (centrika-docs!77). */}
                    <DataTableCell className="whitespace-nowrap text-txt-secondary">
                      {o.submittedAt ? formatDate(o.submittedAt) : '—'}
                    </DataTableCell>
                  </DataTableRow>
                )
              })}
            </DataTableBody>
          </DataTable>
        )}
      </Panel>
      <Pagination
        page={page}
        totalPages={Math.max(1, Math.ceil(total / PAGE_SIZE))}
        onChange={setPage}
      />
    </div>
  )
}
